import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const RMProFormaYieldCurve = ({ yieldCurves }) => {
  const colors = ['hsl(var(--chart-1))', 'hsl(var(--chart-3))', 'hsl(var(--chart-2))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))'];
  const axisTickStyle = { fill: 'hsl(var(--chart-axis))', fontSize: 12 };
  const legendFormatter = (value) => <span className="ace-chart-legend-text">{value}</span>;

  const formatYieldCurveData = (yieldCurves) => {
    const maturities = ['0.25', '0.5', '1', '2', '3', '5', '10', '30'];
    return maturities.map(maturity => ({
      maturity: maturity === '0.25' ? '3M' : maturity === '0.5' ? '6M' : `${maturity}Y`,
      ...Object.fromEntries(
        Object.entries(yieldCurves).map(([date, rates]) => [date, rates[maturity] ? rates[maturity] * 100 : null])
      )
    }));
  };

  // Sort date keys to ensure consistent series ordering
  const sortedYieldCurves = Object.fromEntries(
    Object.entries(yieldCurves || {}).sort(([a], [b]) => new Date(a) - new Date(b))
  );
  const data = formatYieldCurveData(sortedYieldCurves);

  if (!yieldCurves || Object.keys(yieldCurves).length === 0) {
    return (
      <Card className="card-wrapper mt-6">
        <CardHeader>
          <CardTitle className="font-semibold text-primary">Yield Curve Projections</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-foreground">No yield curve data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-wrapper mt-6">
      <CardHeader>
        <CardTitle className="!text-primary">Yield Curve Projections</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--chart-grid))" />
            <XAxis dataKey="maturity" stroke="hsl(var(--chart-axis))" tick={axisTickStyle} />
            <YAxis 
              label={{ value: 'Yield (%)', angle: -90, position: 'insideLeft', fill: 'hsl(var(--muted-foreground))' }}
              domain={['auto', 'auto']}
              stroke="hsl(var(--chart-axis))"
              tick={axisTickStyle}
            />
            <Tooltip
              contentStyle={{
                background: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '12px'
              }}
              labelStyle={{ color: 'hsl(var(--foreground))' }}
              itemStyle={{ color: 'hsl(var(--muted-foreground))' }}
            />
            <Legend formatter={legendFormatter} />
            {(() => {
              const dates = Object.keys(sortedYieldCurves); // already ascending
              if (dates.length === 0) return null;
              const baseColor = colors[0];
              return (
                <>
                  {dates.map((date, idx) => {
                    // Last date = most recent/current → full opacity
                    const isCurrent = idx === dates.length - 1;
                    const strokeOpacity = isCurrent ? 1 : Math.max(0.25, 0.35 + (idx / dates.length) * 0.5);
                    return (
                      <Line
                        key={date}
                        type="monotone"
                        dataKey={date}
                        stroke={baseColor}
                        strokeOpacity={strokeOpacity}
                        strokeWidth={isCurrent ? 3 : 2}
                        dot={false}
                        name={new Date(date).getFullYear().toString()}
                      />
                    );
                  })}
                </>
              );
            })()}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default RMProFormaYieldCurve;